import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const EntityFormModal = ({ isOpen, onClose, onSave, entity, entityType, mode, language }) => {
  const [formData, setFormData] = useState({
    name: '',
    images: [],
    location: { lat: '', lng: '', address: '' },
    serviceTypes: []
  });
  const [errors, setErrors] = useState({});
  const [imagePreview, setImagePreview] = useState([]);

  useEffect(() => {
    if (entity) {
      setFormData({
        name: entity?.name || '',
        images: entity?.images || [],
        location: entity?.location || { lat: '', lng: '', address: '' },
        serviceTypes: entity?.serviceTypes || []
      });
      setImagePreview(entity?.images || []);
    } else {
      setFormData({ name: '', images: [], location: { lat: '', lng: '', address: '' }, serviceTypes: [] });
      setImagePreview([]);
    }
    setErrors({});
  }, [entity, isOpen]);

  const labels = {
    addWorkshop: { en: 'Add New Workshop', ar: 'إضافة ورشة جديدة' },
    editWorkshop: { en: 'Edit Workshop', ar: 'تعديل الورشة' },
    addAgency: { en: 'Add New Agency', ar: 'إضافة وكالة جديدة' },
    editAgency: { en: 'Edit Agency', ar: 'تعديل الوكالة' },
    name: { en: 'Name', ar: 'الاسم' },
    images: { en: 'Images', ar: 'الصور' },
    latitude: { en: 'Latitude', ar: 'خط العرض' },
    longitude: { en: 'Longitude', ar: 'خط الطول' },
    address: { en: 'Address', ar: 'العنوان' },
    serviceTypes: { en: 'Service Types', ar: 'أنواع الخدمات' },
    cancel: { en: 'Cancel', ar: 'إلغاء' },
    save: { en: 'Save', ar: 'حفظ' },
    required: { en: 'This field is required', ar: 'هذا الحقل مطلوب' },
    invalidCoordinates: { en: 'Invalid GPS coordinates', ar: 'إحداثيات GPS غير صحيحة' },
    uploadImages: { en: 'Upload Images', ar: 'تحميل الصور' }
  };

  const workshopServiceOptions = [
    { value: 'Oil Change', label: language === 'en' ? 'Oil Change' : 'تغيير الزيت' },
    { value: 'Brake Repair', label: language === 'en' ? 'Brake Repair' : 'إصلاح الفرامل' },
    { value: 'Engine Diagnostics', label: language === 'en' ? 'Engine Diagnostics' : 'تشخيص المحرك' },
    { value: 'Tire Replacement', label: language === 'en' ? 'Tire Replacement' : 'استبدال الإطارات' },
    { value: 'AC Repair', label: language === 'en' ? 'AC Repair' : 'إصلاح التكييف' },
    { value: 'Paint Job', label: language === 'en' ? 'Paint Job' : 'طلاء' },
    { value: 'Body Work', label: language === 'en' ? 'Body Work' : 'أعمال الهيكل' },
    { value: 'Detailing', label: language === 'en' ? 'Detailing' : 'تنظيف تفصيلي' }
  ];

  const agencyServiceOptions = [
    { value: 'Car Rental', label: language === 'en' ? 'Car Rental' : 'تأجير سيارات' },
    { value: 'Chauffeur Service', label: language === 'en' ? 'Chauffeur Service' : 'خدمة سائق' },
    { value: 'Airport Transfer', label: language === 'en' ? 'Airport Transfer' : 'نقل من المطار' },
    { value: 'Luxury Rentals', label: language === 'en' ? 'Luxury Rentals' : 'تأجير فاخر' },
    { value: 'Long-term Lease', label: language === 'en' ? 'Long-term Lease' : 'إيجار طويل الأجل' },
    { value: 'Corporate Fleet', label: language === 'en' ? 'Corporate Fleet' : 'أسطول الشركات' },
    { value: 'Event Transportation', label: language === 'en' ? 'Event Transportation' : 'نقل الفعاليات' },
    { value: 'Wedding Cars', label: language === 'en' ? 'Wedding Cars' : 'سيارات الزفاف' }
  ];

  const serviceOptions = entityType === 'workshops' ? workshopServiceOptions : agencyServiceOptions;

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = labels?.required?.[language];
    }

    if (!formData?.location?.lat || !formData?.location?.lng) {
      newErrors.location = labels?.required?.[language];
    } else if (isNaN(formData?.location?.lat) || isNaN(formData?.location?.lng)) {
      newErrors.location = labels?.invalidCoordinates?.[language];
    }

    if (!formData?.location?.address?.trim()) {
      newErrors.address = labels?.required?.[language];
    }

    if (formData?.serviceTypes?.length === 0) {
      newErrors.serviceTypes = labels?.required?.[language];
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      onSave(formData);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleLocationChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      location: { ...prev?.location, [field]: value }
    }));
    if (errors?.location || errors?.address) {
      setErrors(prev => ({ ...prev, location: '', address: '' }));
    }
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e?.target?.files);
    const imageUrls = files?.map(file => URL.createObjectURL(file));
    setImagePreview([...imagePreview, ...imageUrls]);
    setFormData(prev => ({ ...prev, images: [...prev?.images, ...imageUrls] }));
  };

  const removeImage = (index) => {
    const newImages = imagePreview?.filter((_, i) => i !== index);
    setImagePreview(newImages);
    setFormData(prev => ({ ...prev, images: newImages }));
  };

  if (!isOpen) return null;

  const getTitle = () => {
    if (entityType === 'workshops') {
      return mode === 'add' ? labels?.addWorkshop?.[language] : labels?.editWorkshop?.[language];
    }
    return mode === 'add' ? labels?.addAgency?.[language] : labels?.editAgency?.[language];
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-card rounded-xl shadow-elevation-4 border border-border w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-foreground">
            {getTitle()}
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-muted transition-smooth"
            aria-label="Close"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <Input
            label={labels?.name?.[language]}
            value={formData?.name}
            onChange={(e) => handleChange('name', e?.target?.value)}
            error={errors?.name}
            required
          />

          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              {labels?.images?.[language]}
            </label>
            <div className="space-y-3">
              {imagePreview?.length > 0 && (
                <div className="grid grid-cols-3 gap-3">
                  {imagePreview?.map((img, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={img}
                        alt={`${entityType} preview ${index + 1}`}
                        className="w-full h-24 object-cover rounded-lg border border-border"
                      />
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="absolute top-1 right-1 p-1 bg-error text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Icon name="X" size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <label className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-border rounded-lg cursor-pointer hover:border-primary hover:bg-primary/5 transition-smooth">
                <Icon name="Upload" size={20} className="text-text-secondary" />
                <span className="text-sm text-text-secondary">{labels?.uploadImages?.[language]}</span>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageUpload}
                  className="hidden"
                />
              </label>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label={labels?.latitude?.[language]}
              type="number"
              step="any"
              value={formData?.location?.lat}
              onChange={(e) => handleLocationChange('lat', e?.target?.value)}
              error={errors?.location}
              required
            />
            <Input
              label={labels?.longitude?.[language]}
              type="number"
              step="any"
              value={formData?.location?.lng}
              onChange={(e) => handleLocationChange('lng', e?.target?.value)}
              error={errors?.location}
              required
            />
          </div>

          <Input
            label={labels?.address?.[language]}
            value={formData?.location?.address}
            onChange={(e) => handleLocationChange('address', e?.target?.value)}
            error={errors?.address}
            required
          />

          <Select
            label={labels?.serviceTypes?.[language]}
            options={serviceOptions}
            value={formData?.serviceTypes}
            onChange={(value) => handleChange('serviceTypes', value)}
            error={errors?.serviceTypes}
            multiple
            searchable
            required
          />

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              fullWidth
              onClick={onClose}
            >
              {labels?.cancel?.[language]}
            </Button>
            <Button
              type="submit"
              variant="default"
              fullWidth
            >
              {labels?.save?.[language]}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EntityFormModal;